import type { LanguageCode } from '~/shared/internationalization';

import type {
    AiTransformationMatrixGrouping,
    AiTransformationStatus,
    AiTransformationSubject,
    AiTransformationTreeView,
} from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';

type Subjected = { readonly filters: AiTransformationFilters; readonly subject: AiTransformationSubject };

type SubjectedKey = readonly [string, string, string, { readonly subject: AiTransformationSubject }, ...unknown[]];

const STRATEGY_ROOT = ['ai-transformation-strategy'] as const;

const architectureKey = (architectureId: string) => [...STRATEGY_ROOT, architectureId] as const;

/** Every key starts with the architecture, so a status change can refresh all of that architecture's reads. */
export const strategyKeys = {
    /** Every read of every architecture, for a change made outside the strategy, such as an automation's adoption. */
    all: STRATEGY_ROOT,
    /** Every read of the architecture, for a change that can touch any of them, such as a position's. */
    architecture: (architectureId: string) => architectureKey(architectureId),
    column: (
        architectureId: string,
        input: Subjected & { readonly status: AiTransformationStatus },
        language: LanguageCode,
    ) => [...architectureKey(architectureId), 'column', input, language] as const,
    columns: (architectureId: string) => [...architectureKey(architectureId), 'column'] as const,
    filters: (architectureId: string, language: LanguageCode) =>
        [...architectureKey(architectureId), 'filters', language] as const,
    /** The Job Architecture one role or level of an architecture connected to none belongs to. */
    groupJobArchitecture: (architectureId: string, grouping: AiTransformationMatrixGrouping, groupId: string) =>
        [...architectureKey(architectureId), 'group-job-architecture', grouping, groupId] as const,
    /** The positions of one role or level; under `itemsAll`, so a status change refreshes them too. */
    groupItems: (
        architectureId: string,
        input: {
            readonly filters: AiTransformationFilters;
            readonly grouping: AiTransformationMatrixGrouping;
            readonly groupId: string;
            readonly subject: AiTransformationSubject;
        },
        language: LanguageCode,
    ) => [...architectureKey(architectureId), 'items', 'group', input, language] as const,
    items: (architectureId: string, input: Subjected & { readonly firstPage: number }, language: LanguageCode) =>
        [...architectureKey(architectureId), 'items', input, language] as const,
    itemsAll: (architectureId: string) => [...architectureKey(architectureId), 'items'] as const,
    leadCandidates: (searchTerm: string) => [...strategyKeys.leadPeople(), 'candidates', searchTerm] as const,
    /** Every search of leads and lead candidates, which a lead change can make stale. */
    leadPeople: () => ['ai-transformation-lead-people'] as const,
    leadSearch: (searchTerm: string) => [...strategyKeys.leadPeople(), 'leads', searchTerm] as const,
    matrix: (
        architectureId: string,
        input: { readonly filters: AiTransformationFilters; readonly grouping: AiTransformationMatrixGrouping },
        language: LanguageCode,
    ) => [...architectureKey(architectureId), 'matrix', input, language] as const,
    matrixAll: (architectureId: string) => [...architectureKey(architectureId), 'matrix'] as const,
    summary: (architectureId: string, input: Subjected, language: LanguageCode) =>
        [...architectureKey(architectureId), 'summary', input, language] as const,
    summaryAll: (architectureId: string) => [...architectureKey(architectureId), 'summary'] as const,
    /** Whether a tile's position, task, role or level is prioritized, and whether the caller may change that. */
    tilePriority: (
        architectureId: string,
        input: {
            readonly filters: AiTransformationFilters;
            readonly id: string;
            readonly kind: AiTransformationMatrixGrouping;
        },
        language: LanguageCode,
    ) => [...architectureKey(architectureId), 'tile-priority', input, language] as const,
    tilePriorityAll: (architectureId: string) => [...architectureKey(architectureId), 'tile-priority'] as const,
    tree: (
        architectureId: string,
        input: { readonly filters: AiTransformationFilters; readonly view: AiTransformationTreeView },
        language: LanguageCode,
    ) => [...architectureKey(architectureId), 'tree', input, language] as const,
    treeAll: (architectureId: string) => [...architectureKey(architectureId), 'tree'] as const,
};

/**
 * Keeps the last result on screen while filters or pages change, but not across subjects, so positions never show
 * under task labels while the tasks load.
 */
export const keepPreviousDataOfSubject =
    (subject: AiTransformationSubject) =>
    <TData>(previous: TData | undefined, previousQuery: { readonly queryKey: SubjectedKey } | undefined) =>
        previousQuery?.queryKey[3].subject === subject ? previous : undefined;

/** Keeps the last matrix on screen while filters or the pick change, but not across groupings. */
export const keepPreviousMatrixOfGrouping =
    (grouping: AiTransformationMatrixGrouping) =>
    <TData>(
        previous: TData | undefined,
        previousQuery:
            | {
                  readonly queryKey: readonly [
                      string,
                      string,
                      string,
                      { readonly grouping: AiTransformationMatrixGrouping },
                      ...unknown[],
                  ];
              }
            | undefined,
    ) =>
        previousQuery?.queryKey[3].grouping === grouping ? previous : undefined;
